import React from 'react';
import { useLanguage } from '@/App';

const BrandsSection = ({ showTitle = false, titleText = '' }) => {
  const { language } = useLanguage();

  const content = {
    tr: {
      title: 'Çalıştığımız Markalar'
    },
    en: {
      title: 'Brands We Work With'
    }
  };

  const t = content[language];
  const displayTitle = titleText || t.title;

  // Brand logos list - 25 brands with custom scales
  const brands = [
    // Row 1 (1-5)
    { name: 'Ebebek', logo: '/brands/ebebek.png', scale: 1 },
    { name: 'Pastel', logo: '/brands/pastel.png', scale: 1 },
    { name: 'Braun', logo: '/brands/braun.png', scale: 1 },
    { name: 'Tecno Mobile', logo: '/brands/tecno.png', scale: 1, whiteBackground: true },
    { name: 'ROCS', logo: '/brands/rocs.png', scale: 1, mobileOrder: 7 },
    // Row 2 (6-10) - New brands
    { name: 'Pınar Online', logo: 'https://customer-assets.emergentagent.com/job_language-toggle-12/artifacts/thog4sy8_pinar.png', scale: 1.67, padding: 'p-2' },
    { name: 'TEMU', logo: 'https://customer-assets.emergentagent.com/job_language-toggle-12/artifacts/xvn0gajy_Temu.png', scale: 1.67, padding: 'p-2', mobileOrder: 5 },
    { name: 'Molfix', logo: '/brands/molfix.png', scale: 1 },
    { name: 'Dekopratik', logo: 'https://customer-assets.emergentagent.com/job_language-toggle-12/artifacts/t80tf94h_dekopratik.png', scale: 1 },
    { name: 'İnce Hesap', logo: 'https://customer-assets.emergentagent.com/job_language-toggle-12/artifacts/rwcb49q3_incehesap.png', scale: 1 },
    // Row 3 (11-15)
    { name: 'Macrocenter', logo: '/brands/macrocenter.png', scale: 1 },
    { name: 'Philips', logo: '/brands/philips.png', scale: 1 },
    { name: 'Oksizinc', logo: '/brands/oksizinc.png', scale: 1 },
    { name: 'Dermotek', logo: '/brands/dermotek.png', scale: 1 },
    { name: 'Prozinc', logo: '/brands/prozinc.png', scale: 1 },
    // Row 4 (16-20) - New brands
    { name: 'ChaCha', logo: 'https://customer-assets.emergentagent.com/job_language-toggle-12/artifacts/hqzjqup9_chacha.png', scale: 1 },
    { name: 'Direct', logo: 'https://customer-assets.emergentagent.com/job_language-toggle-12/artifacts/d475vfta_Direct.png', scale: 1 },
    { name: 'Evdemo', logo: 'https://customer-assets.emergentagent.com/job_language-toggle-12/artifacts/dn58ogck_evdemo.png', scale: 1 },
    { name: 'Greenwich', logo: 'https://customer-assets.emergentagent.com/job_language-toggle-12/artifacts/11wswg4y_Greenwich.png', scale: 1 },
    { name: 'Novakid', logo: 'https://customer-assets.emergentagent.com/job_language-toggle-12/artifacts/ehwsib0z_novakid.png', scale: 1 },
    // Row 5 (21-25)
    { name: 'The Ceel', logo: '/brands/the-ceel.png', scale: 1 },
    { name: 'Vanity', logo: '/brands/vanity.png', scale: 1 },
    { name: 'Nova IT Academy', logo: '/brands/nova-it-academy.png', scale: 1 },
    { name: 'CSS Co', logo: '/brands/css.png', scale: 1 },
    { name: 'Sterilisa', logo: '/brands/sterilisa.png', scale: 1 },
  ];

  // For mobile, create a reordered version
  const getMobileOrderedBrands = () => {
    return brands.map((brand, index) => ({
      ...brand,
      originalIndex: index
    })).sort((a, b) => {
      const orderA = a.mobileOrder ?? a.originalIndex;
      const orderB = b.mobileOrder ?? b.originalIndex;
      return orderA - orderB;
    });
  };

  const mobileOrderedBrands = getMobileOrderedBrands();

  return (
    <section className="py-8 bg-white relative overflow-hidden">
      {/* Background decoration - adjusted for white background */}
      <div className="absolute inset-0 opacity-3 pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Title - shown when showTitle prop is true */}
        {showTitle && (
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl text-black font-bold">
              {displayTitle}
            </h2>
          </div>
        )}
        
        {/* Brands Grid - Desktop version (hidden on mobile) */}
        <div className="hidden sm:grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-8 items-center">
          {brands.map((brand, index) => (
            <div
              key={index}
              className={`flex items-center justify-center ${brand.padding || 'p-6'} rounded-lg hover:bg-gray-50 transition-all duration-300 hover:scale-105 border border-gray-200 shadow-sm bg-white`}
            >
              <img
                src={brand.logo}
                alt={brand.name}
                className="w-full object-contain transition-all"
                style={{
                  height: `${3 * (brand.scale || 1)}rem`
                }}
                loading="lazy"
              />
            </div>
          ))}
        </div>

        {/* Brands Grid - Mobile version (visible only on mobile, with reordered ROCS and TEMU) */}
        <div className="grid grid-cols-2 gap-8 items-center sm:hidden">
          {mobileOrderedBrands.map((brand, index) => (
            <div
              key={index}
              className={`flex items-center justify-center ${brand.padding || 'p-6'} rounded-lg hover:bg-gray-50 transition-all duration-300 hover:scale-105 border border-gray-200 shadow-sm bg-white`}
            >
              <img
                src={brand.logo}
                alt={brand.name}
                className="w-full object-contain transition-all"
                style={{
                  height: `${3 * (brand.scale || 1)}rem`
                }}
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BrandsSection;
